import { useState } from 'react'
import { useNavigate } from 'react-router'

import { Category } from '../../models/Category'
import { RecordFactory } from '../../models/RecordFactory'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Paragraph from '../app/design/Paragraph'
import { ConfirmationModal } from '../common/modals/ConfirmationModal'
import { CategoryPage } from './CategoryPage'

type Props = {
    action: 'new' | 'edit' | 'copy'
}

const viewWillAppear = async ({ owner, urlParams, props: { action } }: PocketComponent<Props>) => {
    const category = await (async (): Promise<Category | null> => {
        switch (action) {
            case 'new': {
                return new Category()
            }
            case 'edit': {
                if (!urlParams.id) {
                    return null
                }
                return (await RecordFactory.getInstance().loadCategory(urlParams.id)) ?? null
            }
            case 'copy': {
                if (!urlParams.id) {
                    return null
                }
                const packageItem =
                    (await RecordFactory.getInstance().loadCategory(urlParams.id)) ?? null
                if (packageItem === null) {
                    return null
                }
                return packageItem.clone({ id: undefined, name: undefined })
            }
        }
    })()
    if (!category) {
        return new RecordNotFoundError()
    }

    const componentItems = await RecordFactory.getInstance().loadItems(owner.id!, [
        'service',
        'product',
    ])

    return {
        category,
        action,
        componentItems,
    }
}

const render = ({
    data,
    base: { logAndDisplayError },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>, Props>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    const { category, componentItems } = data

    const navigate = useNavigate()

    const [isLoading, setIsLoading] = useState(false)
    const [isShowingDeleteConfirmation, setIsShowingDeleteConfirmation] = useState(false)

    const handleSave = async (category: Category, photo?: File) => {
        setIsLoading(true)
        const result = await RecordFactory.getInstance().sendCategory(category, photo)
        setIsLoading(false)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        navigate(`/settings/categories`)
    }

    const handleDelete = async () => {
        const categoryID = category.id
        if (categoryID === undefined) {
            return
        }

        setIsLoading(true)

        const result = await RecordFactory.getInstance().deleteCategory(categoryID)
        setIsLoading(false)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        navigate(`/settings/categories`)
    }

    return (
        <>
            <CategoryPage
                category={category}
                onSave={handleSave}
                onClose={() => navigate(`/settings/categories`)}
                onDelete={
                    data.action === 'new' ? undefined : () => setIsShowingDeleteConfirmation(true)
                }
                isLoading={isLoading}
                componentItems={componentItems}
            />

            <ConfirmationModal
                isOpen={isShowingDeleteConfirmation}
                onClose={() => setIsShowingDeleteConfirmation(false)}
                title={`Delete ${category.name}?`}
                size="small"
                confirmButtonText="Yes, delete"
                shouldHideCancelButton
                isDestructive
                onConfirm={handleDelete}
            >
                <Paragraph>This action cannot be undone.</Paragraph>
            </ConfirmationModal>
        </>
    )
}

export const CategoryWorkflow = usePSComponent(render, viewWillAppear)
